import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReorderIcon from '@mui/icons-material/Reorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DnsIcon from '@mui/icons-material/Dns';
import { RootState } from '../../store';
import { useBrand } from '../../brands/BrandProvider';
import { eventService } from '../../services/eventService';
import {
  disableParishLandingCustomDomain,
  getParishLanding,
  getParishLandingCustomDomain,
  saveParishLanding,
  saveParishLandingCustomDomain,
  verifyParishLandingCustomDomain,
  type ParishLandingData,
  type ParishLandingEventsSortMode,
  type ParishLandingLink,
} from '../../services/parishLandingService';
import type { EventCustomDomain } from '../../services/customDomainService';
import ZuadariaShowsOrderDialog from '../../components/event-workspace/ZuadariaShowsOrderDialog';

const statusMeta: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  pending: { label: 'Aguardando DNS', color: 'warning' },
  active: { label: 'Ativo', color: 'success' },
  disabled: { label: 'Desativado', color: 'default' },
};

type CatalogEvent = { id: string; title: string; slug: string; status: string; coverImage?: string };

const ParishLandingEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const brand = useBrand();
  const { user } = useSelector((state: RootState) => state.auth);
  const isZuadariaProfile =
    brand.key === 'zuadaria' || user?.parish?.brandKey === 'zuadaria';
  const enabled =
    user?.parish?.features?.customDomainPagesEnabled === true || isZuadariaProfile;

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [landing, setLanding] = useState<ParishLandingData | null>(null);
  const [catalog, setCatalog] = useState<CatalogEvent[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainData, setDomainData] = useState<EventCustomDomain | null>(null);
  const [showsOrderOpen, setShowsOrderOpen] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const [landingData, eventsRes, domain] = await Promise.all([
        getParishLanding(),
        eventService.getEvents(user?.parishId ? { parishId: user.parishId } : undefined),
        getParishLandingCustomDomain().catch(() => null),
      ]);
      setLanding({
        ...landingData,
        eventsSortMode: landingData.eventsSortMode || 'startDate',
      });
      const list = eventsRes?.success && Array.isArray(eventsRes.data) ? eventsRes.data : [];
      const mapped: CatalogEvent[] = list.map((e: any) => ({
        id: String(e._id || e.id),
        title: e.title,
        slug: e.slug,
        status: e.status,
        coverImage: e.coverImage || undefined,
      }));
      setCatalog(mapped.filter((e) => ['published', 'test', 'pre_published'].includes(e.status)));
      setDomainData(domain);
      if (domain?.domain) setDomainInput(domain.domain);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar página.');
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.parishId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedSet = useMemo(() => new Set(landing?.eventIds || []), [landing?.eventIds]);

  const previewUrl = useMemo(() => {
    if (domainData?.status === 'active' && domainData.publicUrl) return domainData.publicUrl;
    if (isZuadariaProfile) return '/';
    if (landing?.parishSlug) return `/p/${landing.parishSlug}`;
    return null;
  }, [domainData, landing?.parishSlug, isZuadariaProfile]);

  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const updateProfile = (field: keyof ParishLandingData['profile'], value: string) => {
    if (!landing) return;
    setLanding({ ...landing, profile: { ...landing.profile, [field]: value } });
  };

  const updateTheme = (field: keyof ParishLandingData['theme'], value: string) => {
    if (!landing) return;
    setLanding({ ...landing, theme: { ...landing.theme, [field]: value } });
  };

  const toggleEvent = (eventId: string) => {
    if (!landing) return;
    const ids = landing.eventIds.includes(eventId)
      ? landing.eventIds.filter((id) => id !== eventId)
      : [...landing.eventIds, eventId];
    setLanding({ ...landing, eventIds: ids });
  };

  const setEventsSortMode = (mode: ParishLandingEventsSortMode) => {
    if (!landing) return;
    setLanding({ ...landing, eventsSortMode: mode });
    if (mode === 'custom') setShowsOrderOpen(true);
  };

  const addLink = () => {
    if (!landing) return;
    const links: ParishLandingLink[] = [
      ...landing.links,
      { label: '', url: '', order: landing.links.length },
    ];
    setLanding({ ...landing, links });
  };

  const updateLink = (index: number, field: 'label' | 'url', value: string) => {
    if (!landing) return;
    const links = landing.links.map((l, i) => (i === index ? { ...l, [field]: value } : l));
    setLanding({ ...landing, links });
  };

  const removeLink = (index: number) => {
    if (!landing) return;
    setLanding({
      ...landing,
      links: landing.links.filter((_, i) => i !== index).map((l, order) => ({ ...l, order })),
    });
  };

  const handleSave = async () => {
    if (!landing) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await saveParishLanding({
        template: isZuadariaProfile ? 'comedy_club' : landing.template || 'events_bio',
        published: landing.published,
        profile: landing.profile,
        theme: landing.theme,
        eventIds: landing.eventIds,
        eventsSortMode: isZuadariaProfile
          ? landing.eventsSortMode || 'startDate'
          : undefined,
        links: landing.links.filter((l) => l.label.trim() && l.url.trim()),
      });
      setLanding({
        ...saved,
        eventsSortMode: saved.eventsSortMode || landing.eventsSortMode || 'startDate',
      });
      setSuccess('Página salva.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar.');
    } finally {
      setBusy(false);
    }
  };

  const handleDomainSave = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await saveParishLandingCustomDomain(domainInput);
      setDomainData(saved);
      setSuccess('Domínio salvo. Configure o DNS (A → IP do servidor) e clique em Verificar DNS.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar domínio.');
    } finally {
      setBusy(false);
    }
  };

  const handleDomainVerify = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const verified = await verifyParishLandingCustomDomain();
      setDomainData(verified);
      setSuccess('Domínio verificado e ativo!');
    } catch (err: any) {
      const payload = err?.response?.data;
      if (payload?.data) setDomainData(payload.data);
      setError(payload?.message || 'DNS ainda não está correto.');
    } finally {
      setBusy(false);
    }
  };

  const handleDomainDisable = async () => {
    if (!window.confirm('Desativar o domínio personalizado da página?')) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const disabled = await disableParishLandingCustomDomain();
      setDomainData(disabled);
      setSuccess('Domínio desativado.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao desativar.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copiado!');
    } catch {
      setError('Não foi possível copiar.');
    }
  };

  if (loading || !landing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const domainMeta = domainData ? statusMeta[domainData.status] : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Minha página
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Landing pública com seus eventos (plano personalizado).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {previewUrl && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
            >
              Ver página
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={busy}>
            Salvar
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="Conteúdo" />
          <Tab label="Domínio" />
        </Tabs>
        <Divider />

        {tab === 0 && (
          <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={landing.published}
                  onChange={(e) => setLanding({ ...landing, published: e.target.checked })}
                />
              }
              label="Página publicada"
            />

            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Perfil
              </Typography>
              <TextField
                label="Título"
                value={landing.profile.title}
                onChange={(e) => updateProfile('title', e.target.value)}
                fullWidth
              />
              <TextField
                label="Bio"
                value={landing.profile.bio}
                onChange={(e) => updateProfile('bio', e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="URL do avatar"
                value={landing.profile.avatarUrl}
                onChange={(e) => updateProfile('avatarUrl', e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Cores
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Cor primária"
                  type="color"
                  value={landing.theme.primaryColor || '#0f4a80'}
                  onChange={(e) => updateTheme('primaryColor', e.target.value)}
                  sx={{ width: { xs: '100%', sm: 180 } }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Cor secundária"
                  type="color"
                  value={landing.theme.secondaryColor || '#1e6bb8'}
                  onChange={(e) => updateTheme('secondaryColor', e.target.value)}
                  sx={{ width: { xs: '100%', sm: 180 } }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                {isZuadariaProfile ? 'Shows na página' : 'Eventos na página'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isZuadariaProfile
                  ? 'Defina a ordem da home e, se quiser, quais shows entram. A organização detalhada abre em outra tela.'
                  : 'Selecione e ordene os eventos que aparecem na página. Eventos novos publicados entram automaticamente; desmarque para ocultar.'}
              </Typography>

              {isZuadariaProfile ? (
                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                    <InputLabel id="zuadaria-events-sort-mode">Ordem de exibição</InputLabel>
                    <Select
                      labelId="zuadaria-events-sort-mode"
                      label="Ordem de exibição"
                      value={landing.eventsSortMode || 'startDate'}
                      onChange={(e) =>
                        setEventsSortMode(e.target.value as ParishLandingEventsSortMode)
                      }
                    >
                      <MenuItem value="createdAt">Por ordem de criação do evento</MenuItem>
                      <MenuItem value="startDate">Por data do show</MenuItem>
                      <MenuItem value="custom">Personalizada</MenuItem>
                    </Select>
                  </FormControl>

                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {landing.eventIds.length} show(s) na página
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(landing.eventsSortMode || 'startDate') === 'custom'
                            ? 'Ordem personalizada — use Organizar para arrastar, buscar ou ir à posição.'
                            : (landing.eventsSortMode || 'startDate') === 'createdAt'
                              ? 'Ordem automática por criação.'
                              : 'Ordem automática por data do show.'}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<ReorderIcon />}
                        onClick={() => setShowsOrderOpen(true)}
                        disabled={!catalog.length}
                      >
                        Organizar shows
                      </Button>
                    </Stack>
                  </Paper>

                  {!catalog.length && (
                    <Alert severity="info">
                      Nenhum evento publicado. Crie um evento e volte aqui.{' '}
                      <Button size="small" onClick={() => navigate('/dashboard/eventos')}>
                        Ir para eventos
                      </Button>
                    </Alert>
                  )}
                </Stack>
              ) : (
                <>
                  {!catalog.length && (
                    <Alert severity="info">
                      Nenhum evento publicado. Crie um evento e volte aqui.{' '}
                      <Button size="small" onClick={() => navigate('/dashboard/eventos')}>
                        Ir para eventos
                      </Button>
                    </Alert>
                  )}
                  <List dense>
                    {catalog.map((ev) => {
                      const checked = selectedSet.has(ev.id);
                      return (
                        <ListItem key={ev.id} disablePadding>
                          <ListItemButton onClick={() => toggleEvent(ev.id)}>
                            <ListItemIcon>
                              <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                            </ListItemIcon>
                            <ListItemText primary={ev.title} secondary={ev.status} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  Links extras
                </Typography>
                <Button startIcon={<AddIcon />} onClick={addLink} size="small">
                  Adicionar
                </Button>
              </Stack>
              {landing.links.map((link, index) => (
                <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Rótulo"
                    value={link.label}
                    onChange={(e) => updateLink(index, 'label', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="URL"
                    value={link.url}
                    onChange={(e) => updateLink(index, 'url', e.target.value)}
                    fullWidth
                  />
                  <IconButton onClick={() => removeLink(index)} aria-label="Remover link">
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={2.5} sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <DnsIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Domínio personalizado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A raiz do domínio abre esta landing com seus eventos.
                </Typography>
              </Box>
            </Stack>

            {domainData && domainMeta && (
              <Chip size="small" color={domainMeta.color} label={domainMeta.label} sx={{ alignSelf: 'flex-start' }} />
            )}

            <TextField
              label="Domínio"
              placeholder="eventos.suaigreja.org"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              fullWidth
              helperText="Sem http://. Preferencialmente um subdomínio (ex.: www). DNS: registro A → IP do servidor."
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" onClick={handleDomainSave} disabled={busy || !domainInput.trim()}>
                Salvar domínio
              </Button>
              <Button variant="outlined" onClick={handleDomainVerify} disabled={busy || !domainData}>
                Verificar DNS
              </Button>
              {domainData && domainData.status !== 'disabled' && (
                <Button color="inherit" onClick={handleDomainDisable} disabled={busy}>
                  Desativar
                </Button>
              )}
            </Stack>

            {domainData?.dns && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Configure no seu DNS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {domainData.dns.instructions}
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 72 }}>
                      Tipo
                    </Typography>
                    <Chip size="small" label={domainData.dns.type} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 72 }}>
                      Host
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {domainData.dns.host}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopy(domainData.dns.host)}
                    >
                      Copiar
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 72 }}>
                      Valor
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {domainData.dns.value}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopy(domainData.dns.value)}
                    >
                      Copiar
                    </Button>
                  </Stack>
                </Stack>
                {domainData.lastError && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {domainData.lastError}
                  </Alert>
                )}
              </Paper>
            )}
          </Stack>
        )}
      </Paper>

      {isZuadariaProfile && landing && (
        <ZuadariaShowsOrderDialog
          open={showsOrderOpen}
          onClose={() => setShowsOrderOpen(false)}
          catalog={catalog}
          eventIds={landing.eventIds}
          sortMode={landing.eventsSortMode || 'startDate'}
          onApply={({ eventIds, sortMode }) => {
            setLanding({
              ...landing,
              eventIds,
              eventsSortMode: sortMode,
            });
          }}
        />
      )}
    </Box>
  );
};

export default ParishLandingEditorPage;
